/**
 * AAA co-branded subdomains (aaa / aaa1.rosefarmers.com) — loaded only there, via snippets/aaa-cobrand.liquid.
 *
 * - Applies the AAA discount code (window.RF.aaa.code) to the session.
 * - Gates checkout behind the AAA Member Number modal: the number is validated by
 *   api.majorrocket.com/aaa/validate and the encrypted value stored as the "aaaMemberNumber"
 *   cart attribute (read by the admin order import to award AAA Dollars).
 * - Members arriving from AAA with ?sessiondata=<encrypted number> are validated silently.
 *
 * Checkout paths: native Checkout buttons are intercepted here (window capture, so before the
 * last-chance modal); scripted redirects in rf-theme.js go through RF.beforeCheckout().
 */
(function () {
  "use strict";

  var RF = (window.RF = window.RF || {});
  var cfg = RF.aaa;
  if (!cfg) return;

  var API = "https://api.majorrocket.com/aaa/validate";
  var ATTR = "aaaMemberNumber";
  var COOKIE = "aaamemid";
  var DISCOUNT_KEY = "rf-aaa-discount:" + cfg.code;

  var validated = false;
  var pending = null; // { resolve, reject } while the modal is open
  var bypass = false;

  function getCookie(name) {
    var parts = document.cookie.split(";");
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].replace(/^\s+/, "");
      if (c.indexOf(name + "=") === 0) return c.substring(name.length + 1);
    }
    return "";
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie =
      name + "=" + (value || "") + "; expires=" + expires + "; path=/; SameSite=Lax; Secure";
  }

  // Persist the encrypted member number AAA passes on the landing link
  var sessionData = new URLSearchParams(window.location.search).get("sessiondata");
  if (sessionData) setCookie(COOKIE, sessionData, 365);

  function applyDiscount(force) {
    try {
      if (!force && window.sessionStorage.getItem(DISCOUNT_KEY)) return Promise.resolve();
    } catch (e) {}
    return fetch("/discount/" + encodeURIComponent(cfg.code), { credentials: "same-origin" })
      .then(function () {
        try {
          window.sessionStorage.setItem(DISCOUNT_KEY, "1");
        } catch (e) {}
      })
      .catch(function () {});
  }

  function setCartAttribute(value) {
    var attributes = {};
    attributes[ATTR] = value;
    return fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    });
  }

  /** Resolves with the API response on success, rejects with an error message. */
  function validate(number, isEncrypted) {
    var form = new FormData();
    form.append("memberNumber", number);
    form.append("isEncrypted", isEncrypted ? "true" : "false");
    return fetch(API, { method: "POST", body: form })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data && data.success) return data;
        throw new Error((data && data.error) || "Your Member Number could not be validated.");
      });
  }

  // On load: re-validate a number already on the cart (or from the AAA landing cookie)
  var ready = fetch("/cart.js", { credentials: "same-origin" })
    .then(function (r) {
      return r.json();
    })
    .then(function (cart) {
      var onCart = (cart.attributes && cart.attributes[ATTR]) || "";
      var stored = onCart || getCookie(COOKIE);
      if (!stored) return;
      return validate(stored, true).then(
        function () {
          validated = true;
          if (!onCart) return setCartAttribute(stored);
        },
        function () {
          setCookie(COOKIE, "", -1);
          if (onCart) return setCartAttribute("");
        },
      );
    })
    .catch(function () {});

  applyDiscount(false);

  var modal, form, input, errorEl, submitBtn;

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg || "";
    errorEl.hidden = !msg;
  }

  function openModal() {
    showError("");
    input.value = "";
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    modal.classList.add("is-open");
    document.documentElement.classList.add("rf-upsell-open");
    window.setTimeout(function () {
      input.focus();
    }, 30);
  }

  function closeModal() {
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("is-open");
    document.documentElement.classList.remove("rf-upsell-open");
  }

  /**
   * Resolves once the shopper has a validated AAA Member Number on the cart; rejects if they
   * cancel the modal. Used by every checkout path.
   */
  RF.beforeCheckout = function () {
    return ready.then(function () {
      if (validated) return applyDiscount(true);
      if (!modal) return; // modal markup missing — don't block the sale
      if (pending) pending.reject(new Error("superseded"));
      return new Promise(function (resolve, reject) {
        pending = { resolve: resolve, reject: reject };
        openModal();
      });
    });
  };

  function cancel() {
    closeModal();
    if (pending) pending.reject(new Error("cancelled"));
    pending = null;
  }

  function onSubmit(e) {
    e.preventDefault();
    var num = (input.value || "").replace(/[^0-9]+/g, "");
    if (num.length !== 16) {
      showError("Your AAA Member Number must contain exactly 16 digits. Please re-type your number.");
      input.focus();
      return;
    }
    showError("");
    submitBtn.disabled = true;
    validate(num, false)
      .then(function (data) {
        validated = true;
        if (data.encrypted) setCookie(COOKIE, data.encrypted, 365);
        return setCartAttribute(data.encrypted || num).then(function () {
          return applyDiscount(true);
        });
      })
      .then(function () {
        submitBtn.disabled = false;
        closeModal();
        if (pending) pending.resolve();
        pending = null;
      })
      .catch(function (err) {
        submitBtn.disabled = false;
        input.value = "";
        showError(err && err.message ? err.message : "Your Member Number could not be validated.");
        input.focus();
      });
  }

  // Native Checkout buttons (cart drawer / cart page). Window capture runs before the
  // last-chance modal's document listener, so the member number is asked for first.
  window.addEventListener(
    "click",
    function (e) {
      if (bypass || validated) return;
      var btn = e.target.closest && e.target.closest('button[name="checkout"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      RF.beforeCheckout().then(
        function () {
          if (!btn.isConnected) {
            window.location.href = "/checkout";
            return;
          }
          bypass = true;
          btn.click();
          bypass = false;
        },
        function () {},
      );
    },
    true,
  );

  document.addEventListener("DOMContentLoaded", function () {
    modal = document.querySelector("[data-aaa-modal]");
    if (!modal) return;
    form = modal.querySelector("[data-aaa-form]");
    input = modal.querySelector("[data-aaa-input]");
    errorEl = modal.querySelector("[data-aaa-error]");
    submitBtn = modal.querySelector("[data-aaa-submit]");
    form.addEventListener("submit", onSubmit);
    Array.prototype.forEach.call(modal.querySelectorAll("[data-aaa-cancel]"), function (b) {
      b.addEventListener("click", cancel);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && pending) cancel();
    });
  });
})();
